import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Brush, Cell } from 'recharts'
import classNames from 'classnames'
import random from 'lodash/random'

import getDiagramData from './helpers/getDiagramDataFromTable'
import NavBar from 'components/NavBar'
import { Area as AreaIco, Bar as BarIco, Column, Line as LineIco, Pie as PieIco } from './icons'
import pieColors from './helpers/pieColors'
import styles from './charts.module.scss'

const getChartTypes = t => ([
	{
		name: t('statistic_section.strings.diagram_type', { val: '$t(statistic_section.strings.area_type)' }),
		type: 'area',
		icon: AreaIco
	}, {
		name: t('statistic_section.strings.diagram_type', { val: '$t(statistic_section.strings.bar_type)' }),
		type: 'bar',
		icon: BarIco,
		disabled: true
	}, {
		name: t('statistic_section.strings.diagram_type', { val: '$t(statistic_section.strings.column_type)' }),
		type: 'column',
		icon: Column
	}, {
		name: t('statistic_section.strings.diagram_type', { val: '$t(statistic_section.strings.line_type)' }),
		type: 'line',
		icon: LineIco
	}, {
		name: t('statistic_section.strings.diagram_type', { val: '$t(statistic_section.strings.pie_type)' }),
		type: 'pie',
		icon: PieIco
	}
])

const ChartContainer = ({ data = [], dataKey = '', tableData, ...props }) => {
	const [t] = useTranslation()
	const [chartType, setChartType] = useState('column')
	const chartTypes = getChartTypes(t)
	const navContent = chartTypes.map(({ type, ...nav }, i) => ({
		...nav,
		action: () => setChartType(type),
		className: classNames(styles['nav-item'], { [styles['nav-active']]: type === chartType })
	}))
	let diagramData = data
	let diagramDataKey = dataKey
	if(tableData) {
		const { data: dataFromTable, dataKey: keyFromTable } = getDiagramData(tableData, 'total')
		diagramData = dataFromTable
		diagramDataKey = keyFromTable
	}
	const chartProps = {
		data: diagramData,
		maxBarSize: 47
	}
	const getChart = type => {
		const chartItemProps = {
			dataKey: diagramDataKey,
			fill: 'url(#barGradient)',
			stroke: '#ffd91f'
		}
		let ChartType, ChartItem
		switch(type) {
			case 'area':
				ChartType = AreaChart
				ChartItem = Area
				chartItemProps.type = 'monotone'
				break
			case 'bar':
				ChartType = BarChart
				ChartItem = Bar
				break
			case 'column':
				ChartType = BarChart
				ChartItem = Bar
				break
			case 'line':
				ChartType = LineChart
				ChartItem = Line
				chartItemProps.type = 'monotone'
				break
			case 'pie':
				ChartType = PieChart
				ChartItem = Pie
				return(
					<ChartType>
						<ChartItem
							data={diagramData}
							dataKey={diagramDataKey}
							nameKey='name'
							innerRadius={8}
							fill='#fff'
							stroke='#fff'
							strokeWidth='2px'
						>
							{diagramData.map((_, i) => {
								// Get color by index from defined colors or by random index if index is greater than defined colors length
								const itemColor = i >= pieColors.length ? pieColors[random(pieColors.length - 1)] : pieColors[i]
								return <Cell key={i} fill={itemColor} />
							})}
						</ChartItem>
					</ChartType>
				)
			default:
		}
		return(
			<ChartType {...chartProps} maxBarSize={47}>
				<defs>
					<linearGradient id='barGradient' x1='0' y1='0' x2='0' y2='1'>
						<stop offset={0} stopColor='#ffe45f' stopOpacity={0.7} />
						<stop offset={50} stopColor='#fff1ae' stopOpacity={0.7} />
						<stop offset={100} stopColor='#fffbe6' stopOpacity={0.5} />
					</linearGradient>
				</defs>
				<CartesianGrid fill='#fff' stroke='#cdcdcd' stroke-width='0.5px' vertical={false} />
				<XAxis dataKey='name' tickMargin={10} height={50} />
				<YAxis />
				<Tooltip
					cursor={type !== 'column'}
					wrapperStyle={{ boxShadow: '2px 2px 4px 0 rgba(90, 90, 90, 0.2)', border: 'none' }}
					contentStyle={{ backgroundColor: '#fff', borderRadius: '3px', border: 'none', color: 'black' }}
					formatter={(value, name) => {
						let formattedVal = value
						const numVal = Number(value)
						if(!isNaN(numVal)) {
							formattedVal = numVal.toFixed(2)
						}
						return [formattedVal, name]
					}}
					active
				/>
				{diagramData.length > 20 &&
					<Brush dataKey='name' endIndex={20} height={20} stroke='#3e4c57' fill='#fff' travellerWidth={10} />
				}
				<ChartItem {...chartItemProps} />
			</ChartType>
		)
	}

	return(
		<NavBar navContent={navContent} className={styles.navbar}>
			<ResponsiveContainer height={624}>
				{getChart(chartType)}
			</ResponsiveContainer>
		</NavBar>
	)
}

export default ChartContainer
